import { useCallback, useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../utils/firebase';

const UPDATE_BEER_AVAILABILITY_CALLABLE =
  'callableBeerUpdateBeerAvailability-updateBeerAvailability';

/**
 * Loads brewery group members and updates beer availability via callable.
 */
function useBreweryGroupMembers(breweryGroupId) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    const normalizedGroupId =
      typeof breweryGroupId === 'string' ? breweryGroupId.trim() : '';

    if (!normalizedGroupId) {
      setMembers([]);
      setLoading(false);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const breweriesSnap = await getDocs(
        query(
          collection(db, 'breweries'),
          where('breweryGroupId', '==', normalizedGroupId),
        ),
      );

      const rows = breweriesSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      rows.sort((left, right) =>
        (left.name || left.city || left.id).localeCompare(
          right.name || right.city || right.id,
          undefined,
          { sensitivity: 'base' },
        ),
      );
      setMembers(rows);
    } catch (err) {
      console.error('Error loading brewery group members:', err);
      setError('Failed to load brewery group members');
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [breweryGroupId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { members, loading, error, refresh };
}

function useBeerAvailability() {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const clearError = useCallback(() => {
    setErrorMessage('');
  }, []);

  const updateBeerAvailability = useCallback(async ({
    beerId,
    availableBreweryIds,
    mode = 'replace',
  }) => {
    if (!beerId || !Array.isArray(availableBreweryIds) || availableBreweryIds.length === 0) {
      setErrorMessage('Beer and at least one location are required');
      return null;
    }

    setLoading(true);
    setErrorMessage('');

    try {
      const updateFn = httpsCallable(functions, UPDATE_BEER_AVAILABILITY_CALLABLE);
      const result = await updateFn({ beerId, availableBreweryIds, mode });
      return result.data;
    } catch (err) {
      const message =
        err?.message || 'Failed to update beer availability. Please try again.';
      setErrorMessage(message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    updateBeerAvailability,
    loading,
    errorMessage,
    clearError,
  };
}

export { useBreweryGroupMembers, useBeerAvailability };
